'use client'

import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  memo
} from 'react'

type DemoLayoutContextType = {
  isDemoMessageHidden: boolean
  setIsDemoMessageHidden: (value: boolean) => void
}

const DemoLayoutContext = createContext<DemoLayoutContextType | undefined>(
  undefined
)

type DemoLayoutProps = {
  children: React.ReactNode
  /** 動作環境の告知バー。閉じると同じタブの間は出さない（sessionStorage に記録） */
  renderAlert?: (onClose: () => void) => React.ReactNode
  /** microCMS のプレビュー画面。告知バーを出さず、sessionStorage も読まない */
  isPreview?: boolean
}

/**
 * デモ画面（/demo/[id] と /preview-microcms/demo）の外枠。
 * 画面の高さいっぱいに、告知バー → 中身（ヘッダーと iframe）を縦に積む。
 */
const DemoLayout = ({
  children,
  renderAlert,
  isPreview = false
}: DemoLayoutProps) => {
  const [mounted, setMounted] = useState(false)
  const [isDemoMessageHidden, setIsDemoMessageHidden] = useState(true)

  useEffect(() => {
    // プレビュー画面の場合は常にメッセージを非表示
    if (isPreview) {
      setMounted(true)
      return
    }

    try {
      const storedValue = window.sessionStorage.getItem(
        'lawgue:demo-message-hidden'
      )
      setIsDemoMessageHidden(storedValue === 'true')
    } catch (error) {
      console.error('セッションストレージへのアクセスが拒否されました：', error)
    } finally {
      setMounted(true)
    }
  }, [isPreview])

  const handleClose = useCallback(() => {
    try {
      window.sessionStorage.setItem('lawgue:demo-message-hidden', 'true')
      setIsDemoMessageHidden(true)
    } catch (error) {
      console.error('セッションストレージへのアクセスが拒否されました：', error)
      setIsDemoMessageHidden(true)
    }
  }, [])

  const contextValue = React.useMemo(
    () => ({
      isDemoMessageHidden,
      setIsDemoMessageHidden
    }),
    [isDemoMessageHidden]
  )

  return (
    <DemoLayoutContext.Provider value={contextValue}>
      <div className="h-screen flex flex-col">
        {mounted &&
          !isPreview &&
          !isDemoMessageHidden &&
          renderAlert?.(handleClose)}
        {children}
      </div>
    </DemoLayoutContext.Provider>
  )
}

export default memo(DemoLayout)
